import type { KitchenTicket } from '../domain/hospitalityTypes';
import {
  displayKitchenLine,
  kitchenTicketPrimaryRef,
  kitchenTicketPublicNotes,
  kitchenTicketSource,
  kitchenTicketSourceLabel,
} from '../domain/kitchenTicketPresentation';

export type KitchenTicketComandaPayload = { text: string; html: string };

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function whenLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${mi}`;
}

/** Comanda cocina térmica ~72 mm (58–80 mm). Sin precios: solo producción. */
export function buildKitchenTicketComanda(
  ticket: KitchenTicket,
  opts?: { tableName?: string | null },
): KitchenTicketComandaPayload {
  const code = kitchenTicketPrimaryRef(ticket);
  const source = kitchenTicketSourceLabel(kitchenTicketSource(ticket));
  const when = whenLabel(ticket.createdAt);
  const notes = kitchenTicketPublicNotes(ticket.notes);

  const lineTexts = (ticket.lines ?? []).map((raw) => {
    const line = displayKitchenLine(raw);
    const mods = line.optionsLabel ? ` (${line.optionsLabel})` : '';
    const note = line.lineNote ? ` — ${line.lineNote}` : '';
    return `${raw.qty}× ${line.name}${mods}${note}`;
  });

  const textParts = [
    `Comanda ${code}`,
    source,
    when,
    opts?.tableName ? `Mesa: ${opts.tableName}` : '',
    notes,
    '',
    ...(lineTexts.length ? lineTexts : ['(sin líneas)']),
  ].filter((p, i, arr) => !(p === '' && arr[i - 1] === ''));

  const text = textParts.join('\n');

  const itemRowsHtml = (ticket.lines ?? [])
    .map((raw) => {
      const line = displayKitchenLine(raw);
      const mods = line.optionsLabel
        ? `<div class="mod">${escapeHtml(line.optionsLabel)}</div>`
        : '';
      const note = line.lineNote
        ? `<div class="mod">Nota: ${escapeHtml(line.lineNote)}</div>`
        : '';
      return `<tr><td class="qty">${escapeHtml(String(raw.qty))}×</td><td class="name">${escapeHtml(line.name)}${mods}${note}</td></tr>`;
    })
    .join('');

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<style>
  @page { margin: 3mm; size: 72mm auto; }
  @media print { body { margin: 0; } }
  body {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px; color: #111; margin: 0; padding: 6px;
    max-width: 72mm; width: 72mm; box-sizing: border-box;
  }
  .code {
    text-align: center; font-size: 42px; font-weight: 800;
    letter-spacing: 0.12em; line-height: 1.05; margin: 2px 0 6px;
  }
  .meta { text-align: center; font-size: 12px; margin: 1px 0; }
  .notes { font-weight: 700; margin: 6px 0 2px; font-size: 13px; }
  .sep { border: none; border-top: 1px dashed #111; margin: 8px 0; }
  table { width: 100%; border-collapse: collapse; }
  td.qty {
    width: 2.4em; white-space: nowrap; font-weight: 800;
    font-size: 16px; vertical-align: top; padding: 3px 0;
  }
  td.name { word-break: break-word; font-weight: 700; font-size: 15px; padding: 3px 0; }
  td.name .mod { font-size: 12px; font-weight: 600; margin-top: 2px; color: #222; }
</style></head><body>
  <div class="code">${escapeHtml(code)}</div>
  <div class="meta">${escapeHtml(source)}${when ? ` · ${escapeHtml(when)}` : ''}</div>
  ${opts?.tableName ? `<div class="meta">Mesa ${escapeHtml(opts.tableName)}</div>` : ''}
  ${notes ? `<div class="notes">${escapeHtml(notes)}</div>` : ''}
  <hr class="sep"/>
  <table>${itemRowsHtml || '<tr><td>(sin líneas)</td></tr>'}</table>
</body></html>`;

  return { text, html };
}
